import * as STD from '@emersya/mpa-std-lib';

class ManagerManifest extends Manifest {
	// Map the manifest root component's instance to the manifest manager
	static readonly store = new Map<Component<any, any>, ManagerManifest>();
	declare readonly type: 'manager';
	declare readonly inheritedManifest: ManagerManifest | null;

	async instantiate(): Promise<Component<any, any>> {
		const root = <Component.ITreeNode>this.treeNodes[0];
		const listOfAllUsedComponentsTypes = (() => {
			const o = new Set<string>;
			const ns = Array.from(this.treeNodes);
			while (ns.length) {
				const n = <Component.ITreeNode>ns.shift();
				o.add(n.type);
				ns.push(...n.childs);
			}
			return (
				<Component.TAnyConcrete[]>
					Array.from(o).
					map(x => Manager.storeTypes.get(x)).
					filter(x => x !== undefined)
			);
		})();
		for (const usedComponentType of listOfAllUsedComponentsTypes) {
			if (!usedComponentType.isFullyDeclared) {
				usedComponentType.finalizeDeclaration();
			}
		}
		const out = await Manager.instantiate({
			type: root.type,
			id: root.id,
			configuration: root.configuration || {},
			isSlot: root.isSlot,
			tree: null,
			declaredSlot: null,
			isRoot: true
		});
		ManagerManifest.store.set(out, this);
		const stack: [Component<any, any>, Component.ITreeNode][] = (
			root.childs.map(x => [out, x])
		);
		const hasDeferedConfigurations = Object.keys(this.deferedConfigurations).length > 0;
		while (stack.length) {
			const [parent, node] = <typeof stack[0]>stack.shift();
			const configuration = Object.assign({}, node.configuration);
			if (hasDeferedConfigurations) {
				const childPath = parent.component_getPath() + '.' + node.id;
				const configurationExtension = this.deferedConfigurations[childPath];
				if (configurationExtension) {
					STD.object.deepMergeAssign(configuration, [configurationExtension]);
				}
			}
			node.slot = node.slot || 'default';
			const component = await parent.component_instantiateOuterChild({
				type: node.type,
				id: node.id,
				isSlot: node.isSlot,
				slot: node.slot,
				declaredSlot: node.declaredSlot,
				tree: out,
				configuration
			});
			for (const child of node.childs) {
				stack.push([component, child]);
			}
		}
		await out.component_initialize();
		return (out);
	};

	protected override initializeManifestDetails(manifest: Element): void {
		const ecommerceNode = Array.from(manifest.children).find(x => x.tagName === 'ecommerce');
		manifest.details = manifest.details || {};
		manifest.details.ecommerce = ecommerceNode || null;
		super.initializeManifestDetails(manifest);
	};

	protected constructor(
		source: string,
		initialXML: Element,
		type: string,
		inheritedManifest: ManagerManifest | null
	) {
		super(source, initialXML, type, inheritedManifest);
		Manager.setManifest(this);
		this.dataSourcesGetter.ecommerce = this.ecommerceGetter.bind(this);
		(<any>window).managerManifest = this;
	};

	private ecommerceGetter(target: string) {
		if (!this.finalXML.details) {
			this.throwLibraryError('FinalXML details not initialized!', {
				finalXML: this.finalXML
			});
		}
		const root = <Element | null>this.finalXML.details.ecommerce;
		if (!root) {
			this.throwUserError('Cannot use ecommerce mapping if ecommerce not used!');
		}
		const [_path, property] = <[string, string]>target.split(':');
		const [productName, ...partsPath] = <[string, ...string[]]>_path.split('.');
		const productsNodes = Array.from(root.children).filter(
			x => x.tagName === 'product'
		);
		const productNode = productsNodes.find(x => x.id === productName);
		if (!productNode) {
			this.throwUserError('Product not found!');
		}
		let partNode = productNode;
		for (const partId of partsPath) {
			const _partNode = (
				Array.from(partNode.children).filter(x => x.tagName === 'part').find(x => x.id === partId)
			);
			if (!_partNode) {
				this.throwUserError('Part not found!');
			}
			partNode = _partNode;
		}
		switch (property) {
			case 'options' : {
				const optionsNode = Array.from(partNode.children).find(x => x.tagName === 'options');
				if (!optionsNode) {
					this.throwUserError('There no options!');
				}
				return (Array.from(optionsNode.children));
			}
			case 'parts' : {
				return Array.from(partNode.children).filter(x => x.tagName === 'part');
			}
		}
		this.throwUserError('Nothing to do');
	};
};
namespace ManagerManifest {

}
//@ts-ignore
ManagerManifest.prototype.type = 'manager';
export default ManagerManifest;
import Component from '../Component';
import Manager from '../Manager';
import Manifest from '../Manifest';

Manifest.ManagerManifest = ManagerManifest;
